'use client'
import * as React from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { cn } from '../lib/utils'
import { Button } from './button'
import { Badge } from './badge'

/**
 * `FilterBar` (FO-060) — een filterbalk die op desktop wrapt (geen afgekapte rij)
 * en op mobiel inklapbaar is achter een "Filters"-knop. De controls renderen
 * één keer (in-place uitklappen, geen DOM-duplicatie/portal) zodat controlled
 * inputs en hun handlers ongemoeid blijven; backoffice blijft desktop-first.
 */
export interface FilterBarProps {
  /** De filter-controls (selects/inputs/pickers). */
  children: React.ReactNode
  /** Mobiel achter een "Filters"-knop inklappen (default: false = altijd zichtbaar, wrappend). */
  collapsible?: boolean
  /** Aantal actieve filters — toont een badge op de mobiele knop. */
  activeCount?: number
  /** Label van de mobiele knop. */
  label?: string
  /** Inhoud die ook mobiel altijd zichtbaar blijft (bv. een zoekveld), naast de knop. */
  leading?: React.ReactNode
  className?: string
}

export function FilterBar({
  children,
  collapsible = false,
  activeCount = 0,
  label = 'Filters',
  leading,
  className,
}: FilterBarProps) {
  const [open, setOpen] = React.useState(false)

  if (!collapsible) {
    return (
      <div className={cn('flex flex-wrap items-end gap-2 md:gap-3', className)}>
        {leading}
        {children}
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {leading}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="md:hidden"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          {label}
          {activeCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {activeCount}
            </Badge>
          )}
        </Button>
      </div>
      {/* Desktop: altijd zichtbaar en wrappend; mobiel: alleen wanneer uitgeklapt. */}
      <div
        className={cn(
          'flex-wrap items-end gap-2 md:flex md:gap-3',
          open ? 'flex' : 'hidden',
        )}
      >
        {children}
      </div>
    </div>
  )
}
